<template>
  <div class="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center p-4 z-50 transition-opacity duration-300 animate-modalFade">
    <div class="w-full max-w-md bg-gray-800 text-gray-300 rounded-lg p-2 sm:p-5 flex flex-col gap-4 relative shadow-lg border border-gray-700 animate-scaleIn">
      <!-- Header -->
      <div class="flex justify-between items-center">
        <h3 class="text-lg font-semibold text-gray-100 flex items-center gap-2">
          <i class="fas fa-bomb text-indigo-400 text-xs"></i>
          ติดตั้งระเบิด
        </h3>
        <button @click="$emit('close')" class="text-gray-300 hover:text-indigo-300 group">
          <i class="fas fa-times text-base group-hover:scale-110 transition-transform duration-200"></i>
        </button>
      </div>

      <!-- Main Content -->
      <div class="overflow-y-auto scrollbar-thin" id="style-1">
        <!-- Loading -->
        <div v-if="isLoading" class="flex flex-col items-center py-8">
          <div class="animate-spin rounded-full h-12 w-12 border-t-4 border-red-600"></div>
          <p class="text-gray-300 text-sm mt-4">กำลังโหลดข้อมูล...</p>
        </div>

        <!-- Error -->
        <div v-else-if="error" class="flex flex-col gap-4">
          <div class="text-center text-red-300 py-6 text-sm bg-gray-800 border border-gray-600 rounded-md">
            {{ error }}
          </div>
          <button
            @click="$emit('close')"
            class="bg-gray-700 hover:bg-gray-600 text-white text-xs sm:text-sm py-2 px-4 rounded-md transition-colors duration-200 font-semibold border border-gray-600 flex items-center justify-center gap-2"
          >
            <i class="fas fa-times text-xs"></i> ปิด
          </button>
        </div>

        <!-- Result -->
        <div v-else-if="showResult" class="flex flex-col gap-4">
          <div class="p-2 bg-gray-800 border rounded-md transition-all duration-200 border-l-4 border-blue-400">
            <div class="flex items-center gap-2 text-blue-400 text-xs sm:text-sm">
              <i class="fas fa-check"></i>
              <span class="text-gray-300">ใช้ {{ item.name || 'ไอเทม' }} เพื่อติดตั้งระเบิดที่ห้อง {{ selectedRoom }}</span>
            </div>
          </div>
          <!-- Display bomb placed count -->
          <div class="text-center text-gray-300 text-xs sm:text-sm">
            จำนวนครั้งที่ติดตั้งระเบิดสำหรับเป้าหมายนี้: {{ bombPlacedCount }}/1
          </div>
          <button
            @click="$emit('close')"
            class="bg-gray-700 hover:bg-gray-600 text-white text-xs sm:text-sm py-2 px-4 rounded-md transition-colors duration-200 font-semibold border border-gray-600 flex items-center justify-center gap-2 w-full"
          >
            <i class="fas fa-times text-xs"></i> ปิด
          </button>
        </div>

        <!-- Content -->
        <div v-else class="flex flex-col gap-4">
          <!-- Opponent -->
          <div v-if="hasTarget" class="bg-gray-800 rounded-md p-1.5 flex items-center border border-gray-700 transition-all duration-200 hover:border-indigo-600 hover:bg-gray-900 cursor-pointer gap-2">
            <img
              :src="displayImage"
              alt="Opponent"
              class="w-9 h-9 rounded-md object-cover border-2 border-gray-600"
            />
            <div class="flex flex-col">
              <h4 class="text-gray-300 text-xs sm:text-sm font-semibold">{{ displayName }}</h4>
              <p class="text-gray-300 text-xs">จำนวนครั้งที่ติดตั้งระเบิด: {{ bombPlacedCount }}/1</p>
            </div>
          </div>
          <div v-else class="text-yellow-400 text-center text-xs sm:text-sm">
            <i class="fas fa-exclamation-triangle mr-2 text-yellow-400"></i> ต้องค้นหาผู้รอดชีวิตในเมนูก่อนใช้งาน
          </div>

          <!-- Item -->
          <div class="bg-gray-800 rounded-md p-1.5 flex items-center border border-gray-700 transition-all duration-200 hover:border-indigo-600 hover:bg-gray-900 cursor-pointer gap-2">
            <img
              :src="item.img || defaultItemImg"
              alt="Item"
              class="w-9 h-9 rounded-md object-cover border-2 border-gray-600"
              @error="handleImageError"
            />
            <div class="flex flex-col">
              <h4 class="text-gray-300 text-xs sm:text-sm font-semibold truncate">{{ item.name || 'Unknown' }}</h4>
              <p class="text-gray-300 text-xs">ความเสียหายจากระเบิด: {{ item.damage || 'N/A' }}</p>
            </div>
          </div>

          <!-- Rooms -->
          <div v-if="hasTarget">
            <p class="text-gray-300 text-xs sm:text-sm text-center mb-2">เลือกห้องที่ต้องการติดตั้งระเบิด</p>
            <div class="grid grid-cols-5 gap-1 sm:gap-1.5">
              <button
                v-for="roomNum in roomCount"
                :key="roomNum"
                @click="selectRoom(roomNum)"
                class="bg-gray-800 p-1 rounded-md border border-gray-700 hover:border-indigo-600 hover:bg-gray-900 transition-all duration-200 flex flex-col items-center justify-center"
                :class="{ 'border-2 border-indigo-600': selectedRoom === roomNum }"
                :disabled="isInstalling || bombPlacedCount >= 1"
              >
                <i class="fas fa-door-closed text-gray-300 text-xs"></i>
                <span class="text-xs text-gray-300">{{ roomNum }}</span>
              </button>
            </div>
          </div>

          <!-- Warning -->
          <div class="text-yellow-400 text-xs sm:text-sm text-center">
            <i class="fas fa-exclamation-triangle mr-2 text-yellow-400"></i>
            การติดตั้งระเบิดจะลบไอเทมนี้ทันทีและไม่สามารถย้อนกลับได้
          </div>

          <!-- Countdown -->
          <div v-if="isInstalling" class="flex flex-col gap-3 mt-2">
            <p class="text-center text-gray-300 text-xs sm:text-sm">กำลังติดตั้งระเบิด...</p>

            <!-- Big number with urgency pulse when under 3s -->
            <div
              class="text-center font-bold"
              :class="[
                timeLeft <= 3 ? 'text-red-400 animate-pulse' : 'text-gray-300',
              ]"
            >
              <span class="text-2xl">{{ formattedTime }}</span>
            </div>

            <!-- Progress bar -->
            <div class="relative">
              <div class="bg-gray-800 border border-gray-700 rounded-md h-2">
                <div
                  class="h-full rounded-md transition-all duration-200"
                  :style="{ width: progressPct + '%' }"
                  :class="timeLeft <= 3 ? 'bg-red-600' : 'bg-indigo-600'"
                ></div>
              </div>
              <div class="text-center mt-1">
                <span class="text-xs text-gray-300">ความคืบหน้า: {{ Math.floor(progressPct) }}%</span>
              </div>
            </div>
          </div>

          <!-- Actions -->
          <div class="flex flex-col gap-2">
            <button
              @click="startInstall"
              class="bg-indigo-600 hover:bg-indigo-700 text-white text-xs sm:text-sm py-2 px-4 rounded-md transition-colors duration-200 flex items-center justify-center gap-2 border border-indigo-700"
              :disabled="isInstalling || !hasTarget || !selectedRoom || bombPlacedCount >= 1"
            >
              <span v-if="!isInstalling"><i class="fas fa-bomb text-xs"></i> ยืนยันติดตั้งระเบิด</span>
              <span v-else class="flex items-center gap-2">
                <div class="animate-spin rounded-full h-4 w-4 border-t-2 border-white"></div>
                กำลังติดตั้ง...
              </span>
            </button>

            <button
              @click="$emit('close')"
              class="bg-gray-700 hover:bg-gray-600 text-white text-xs sm:text-sm py-2 px-4 rounded-md transition-colors duration-200 flex items-center justify-center gap-2 border border-gray-600"
            >
              <i class="fas fa-times text-xs"></i> ยกเลิก
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { projectFirestore, projectFunctions } from '@/firebase/config';
import getUser from '@/composables/getUser';
import { usePlayerStore } from '@/composables/usePlayerStore';
import { useInventoryStore } from '@/composables/useInventoryStore';
import gameItems from '@/data/gameItems.json';
import defaultItemImg from '@/assets/default-item.png';

export default {
  name: 'InstallBombModal',
  props: {
    itemId: { type: String, required: true },
    item: { type: Object, default: null }
  },
  emits: ['close', 'bomb-success'],
  setup(props, { emit }) {
    const { user } = getUser();
    const playerStore = usePlayerStore();
    const isLoading = ref(true);
    const error = ref(null);
    const isInstalling = ref(false);
    const hasTarget = ref(false);
    const opponent = ref({});
    const characterPublic = ref({});
    const item = ref({});
    const selectedRoom = ref(null);
    const roomCount = ref(0);
    const bombPlacedCount = ref(0);
    const showResult = ref(false);

    // Countdown
    const totalDuration = ref(10); // seconds base
    const timeLeft = ref(0); // seconds remaining
    let rafId = null;
    let startTimestamp = null;
    let effectiveDuration = 0;

    const anonymousImage = 'https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/character2%2Fanonymouse.png?alt=media&token=25259b2d-03f1-49fb-b70c-054cd88d2a86';

    const displayName = computed(() => {
      if (!opponent.value || characterPublic.value?.building === 1 || characterPublic.value?.building === 2) return 'บุคคลลึกลับ';
      return opponent.value.characterName || 'บุคคลลึกลับ';
    });

    const displayImage = computed(() => {
      if (!opponent.value || characterPublic.value?.building === 1 || characterPublic.value?.building === 2) return anonymousImage;
      return opponent.value.imageUrl || anonymousImage;
    });

    const formattedTime = computed(() => {
      const t = Math.max(0, timeLeft.value);
      const minutes = Math.floor(t / 60);
      const seconds = Math.floor(t % 60);
      const ms = Math.floor((t - Math.floor(t)) * 1000);
      const two = (n) => (n < 10 ? '0' + n : '' + n);
      const three = (n) => (n < 10 ? '00' + n : n < 100 ? '0' + n : '' + n);
      return `${two(minutes)}:${two(seconds)}.${three(ms)}`;
    });

    const progressPct = computed(() => {
      const elapsed = Math.max(0, effectiveDuration - timeLeft.value);
      return Math.min(100, (elapsed / effectiveDuration) * 100 || 0);
    });

    const handleImageError = (e) => { e.target.src = defaultItemImg; };

    const instanceId = computed(() => props.item?.instanceId || props.item?.id || props.itemId);

    const inventoryStore = useInventoryStore();

    const hydrateItem = async () => {
      if (props.item?.name) {
        item.value = props.item;
        return true;
      }

      const bagData = inventoryStore.bagData;
      const bagItem = bagData?.items?.[instanceId.value] || null;
      if (!bagItem) {
        error.value = 'ไม่พบข้อมูลไอเทม';
        return false;
      }

      const template = gameItems[bagItem.templateId] || {};
      const states = bagItem.states || {};
      item.value = {
        instanceId: instanceId.value,
        id: instanceId.value,
        templateId: bagItem.templateId,
        states,
        ...template,
        ...states,
        name: states.name || template.name || bagItem.templateId,
        type: states.type || template.type || '',
      };
      return true;
    };

    const fetchData = async () => {
      try {
        const hasItem = await hydrateItem();
        if (!hasItem) {
          return;
        }
        // Private
        const data = playerStore.characterPrivateData;
        if (data) {
          bombPlacedCount.value = data.bombPlacedCount || 0;
          const assassinateSurvivorUid = data.assassinateSurvivorUid;
          if (assassinateSurvivorUid) {
            hasTarget.value = true;
            const opponentDoc = await projectFirestore.collection('characters').doc(assassinateSurvivorUid).get();
            if (opponentDoc.exists) {
              opponent.value = opponentDoc.data();
              roomCount.value = opponent.value.room || 1;
            } else {
              error.value = 'ไม่พบข้อมูลเป้าหมาย';
              hasTarget.value = false;
            }
          } else {
            hasTarget.value = false;
          }
        } else {
          error.value = 'ไม่พบข้อมูลส่วนตัว';
          hasTarget.value = false;
        }
        // Fetch character public data from store
        characterPublic.value = playerStore.characterData || {};
      } catch (err) {
        error.value = 'เกิดข้อผิดพลาดในการโหลดข้อมูล: ' + err.message;
        hasTarget.value = false;
      } finally {
        isLoading.value = false;
      }
    };

    const selectRoom = (room) => {
      if (bombPlacedCount.value >= 1) return;
      selectedRoom.value = room;
    };

    // Smooth countdown via requestAnimationFrame
    const runCountdown = (durationSec) => {
      cancelCountdown();
      effectiveDuration = durationSec;
      startTimestamp = null;

      const step = (ts) => {
        if (!startTimestamp) startTimestamp = ts;
        const elapsedMs = ts - startTimestamp;
        const remaining = Math.max(0, effectiveDuration - elapsedMs / 1000);
        timeLeft.value = remaining;

        if (remaining > 0) {
          rafId = window.requestAnimationFrame(step);
        } else {
          timeLeft.value = 0;
        }
      };
      rafId = window.requestAnimationFrame(step);
    };

    const cancelCountdown = () => {
      if (rafId) {
        window.cancelAnimationFrame(rafId);
        rafId = null;
      }
    };

    const startInstall = async () => {
      if (!selectedRoom.value || bombPlacedCount.value >= 1) return;
      isInstalling.value = true;
      showResult.value = false;

      // Randomize a little for tension (10.2–11.5s)
      const randomized = 10 + Math.random() * 1.5 + 0.2;
      totalDuration.value = randomized;
      timeLeft.value = randomized;
      runCountdown(randomized);

      try {
        const gameActions = projectFunctions.httpsCallable('gameActions');
        await gameActions({ action: 'installBomb', instanceId: instanceId.value, selectedRoom: selectedRoom.value });
        // Update bomb placed count after successful call
        const charPrivateData = playerStore.characterPrivateData;
        if (charPrivateData) {
          bombPlacedCount.value = charPrivateData.bombPlacedCount || 0;
        }
        showResult.value = true;
        emit('bomb-success', instanceId.value);
      } catch (err) {
        error.value = err?.message || 'เกิดข้อผิดพลาดในการติดตั้งระเบิด';
      } finally {
        cancelCountdown();
        isInstalling.value = false;
      }
    };

    onMounted(() => {
      if (user.value?.uid) {
        playerStore.init(user.value.uid);
      }
      fetchData();
    });
    onUnmounted(cancelCountdown);

    return {
      // state
      isLoading,
      error,
      isInstalling,
      hasTarget,
      opponent,
      characterPublic,
      item,
      selectedRoom,
      roomCount,
      bombPlacedCount,
      // countdown
      timeLeft,
      formattedTime,
      progressPct,
      defaultItemImg,
      // result
      showResult,
      // computed
      displayName,
      displayImage,
      // methods
      startInstall,
      selectRoom,
      handleImageError
    };
  }
};
</script>

<style scoped>
/* Thin scrollbar (custom) */
.scrollbar-thin::-webkit-scrollbar { width: 6px; }
.scrollbar-thin::-webkit-scrollbar-track { background: #111827; border-radius: 8px; }
.scrollbar-thin::-webkit-scrollbar-thumb { background: #374151; border-radius: 8px; }
.scrollbar-thin::-webkit-scrollbar-thumb:hover { background: #4b5563; }
.scrollbar-thin { scrollbar-width: thin; scrollbar-color: #374151 #111827; }

/* Animations */
@keyframes modalFade { from { opacity: 0; } to { opacity: 1; } }
.animate-modalFade { animation: modalFade 0.25s ease-out; }

@keyframes scaleIn { from { transform: scale(0.98); opacity: 0; } to { transform: scale(1); opacity: 1; } }
.animate-scaleIn { animation: scaleIn 0.25s ease-out; }

@keyframes fadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
.animate-fadeIn { animation: fadeIn 0.25s ease-out; }

/* Progress bar */
.progress-container {
  position: relative;
  height: 18px;
  border-radius: 10px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  width: 0%;
  background: linear-gradient(90deg, #3730a3, #4f46e5, #6366f1);
  background-size: 200% 100%;
  animation: sheen 1.1s linear infinite;
  transition: width 120ms linear;
}

.progress-active { filter: none; }
.progress-danger { background: #ef4444; animation: none; }

@keyframes sheen {
  0% { background-position: 200% 0; }
  100% { background-position: 0 0; }
}

.progress-caption {
  margin-top: 6px;
  text-align: center;
}

/* Urgency pulse for last seconds */
@keyframes urgentPulse {
  0% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.06); opacity: 0.8; }
  100% { transform: scale(1); opacity: 1; }
}
.pulse-urgent { animation: urgentPulse 0.6s ease-in-out infinite; }
</style>
